import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Save, Search, Mic, Plus, X, Trash2, Play, Square } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components_dashboard/ui/alert-dialog';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components_dashboard/ui/select';
import { Switch } from '../components_dashboard/ui/switch';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const NEW_VOICE_ID = '__new__';
const CUSTOM_TTS_TYPES = new Set(['gpu', 'gpu-tts']);
const GENDER_UNSET_VALUE = '__unset_gender__';
const GENDER_OPTIONS = ['Female', 'Male'];
// Providers the voice agent can build a TTS service for. Must match
// TTS_OVERRIDE_PROVIDERS in techladder-core (admin/service.py); the API rejects others.
const OVERRIDE_PROVIDER_OPTIONS = [
  { value: 'cartesia', label: 'Cartesia' },
  { value: 'shunyalabs', label: 'ShunyaLabs' },
  { value: 'omnivoice', label: 'OmniVoice' },
  { value: 'rumik', label: 'Rumik' },
];
const OVERRIDE_PROVIDER_UNSET_VALUE = '__unset_override_provider__';
const createEmptyCustomTtsVoice = () => ({
  local_voice_name: '',
  provider_voice: '',
  gender: '',
  actual_pro_voices: '',
});

const EMPTY_VOICE_DRAFT = {
  id: '',
  local_voice_name: '',
  active: false,
  provider: '',
  provider_voice: '',
  gender: '',
  actual_pro_voices: '',
  type: '',
  model_name: '',
  token_id: '',
  language: '',
  preview_url: '',
  override_provider: false,
  override: { provider: '', provider_voice: '' },
  __isNew: true,
  __createMode: 'voice',
  voice_variants: [createEmptyCustomTtsVoice()],
};

export default function VoiceDashboard() {
  const { token, user, refreshUser } = useAuth();
  const [voices, setVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [createDraftMode, setCreateDraftMode] = useState('voice');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [playingPreviewId, setPlayingPreviewId] = useState(null);
  const previewAudioRef = useRef(null);

  const handlePreviewPlay = useCallback((id, url) => {
    if (!url) {
      toast.error('No preview URL set for this voice.');
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (playingPreviewId === id) {
      setPlayingPreviewId(null);
      return;
    }
    const audio = new Audio(url);
    previewAudioRef.current = audio;
    audio.onended = () => setPlayingPreviewId(null);
    audio.onerror = () => {
      setPlayingPreviewId(null);
      toast.error('Unable to play this preview URL.');
    };
    audio.play().catch(() => {
      setPlayingPreviewId(null);
      toast.error('Unable to play this preview URL.');
    });
    setPlayingPreviewId(id);
  }, [playingPreviewId]);

  useEffect(() => () => {
    previewAudioRef.current?.pause();
  }, []);

  const filteredVoices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return voices;
    return voices.filter((entry) =>
      String(entry.local_voice_name || '').toLowerCase().includes(query)
      || String(entry.gender || '').toLowerCase().includes(query)
      || String(entry.provider || '').toLowerCase().includes(query)
      || String(entry.model_name || '').toLowerCase().includes(query)
    );
  }, [searchQuery, voices]);

  const isCreatingVoice = Boolean(draft?.__isNew);
  const isCustomTtsDraft = CUSTOM_TTS_TYPES.has(String(draft?.type || '').toLowerCase()) || draft?.__createMode === 'custom_tts';

  const fetchVoices = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingVoices(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/voices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.voices) ? response.data.voices : [];
      setVoices(rows);
      setSelectedVoiceId((current) => {
        if (current === NEW_VOICE_ID) return current;
        if (current && rows.some((entry) => entry.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (error) {
      console.error('Failed to fetch voices', error);
      toast.error(error.response?.data?.detail || 'Failed to load voices.');
    } finally {
      setLoadingVoices(false);
    }
  }, [token]);

  const fetchVoiceDetails = useCallback(async (voiceId) => {
    if (!token || !voiceId) return;
    try {
      setLoadingVoice(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/voices/${voiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedVoice(response.data);
      setDraft(response.data);
    } catch (error) {
      console.error('Failed to fetch voice details', error);
      toast.error(error.response?.data?.detail || 'Failed to load voice details.');
    } finally {
      setLoadingVoice(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  useEffect(() => {
    if (selectedVoiceId === NEW_VOICE_ID) {
      setSelectedVoice(null);
      setDraft({
        ...EMPTY_VOICE_DRAFT,
        type: createDraftMode === 'custom_tts' ? 'gpu-tts' : '',
        __createMode: createDraftMode,
      });
      setLoadingVoice(false);
    } else if (selectedVoiceId) {
      fetchVoiceDetails(selectedVoiceId);
    } else {
      setSelectedVoice(null);
      setDraft(null);
    }
  }, [createDraftMode, fetchVoiceDetails, selectedVoiceId]);

  const handleCreateDraft = (mode = 'voice') => {
    const isCustomTts = mode === 'custom_tts';
    setCreateDraftMode(mode);
    setSelectedVoiceId(NEW_VOICE_ID);
    setSelectedVoice(null);
    setDraft({
      ...EMPTY_VOICE_DRAFT,
      type: isCustomTts ? 'gpu-tts' : '',
      __createMode: mode,
    });
  };

  const handleFieldChange = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleOverrideFieldChange = (field, value) => {
    setDraft((current) => ({
      ...current,
      override: { ...(current?.override || {}), [field]: value },
    }));
  };

  const handleCustomTtsVoiceFieldChange = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      voice_variants: (current?.voice_variants || []).map((entry, entryIndex) => (
        entryIndex === index ? { ...entry, [field]: value } : entry
      )),
    }));
  };

  const handleAddCustomTtsVoice = () => {
    setDraft((current) => ({
      ...current,
      voice_variants: [...(current?.voice_variants || []), createEmptyCustomTtsVoice()],
    }));
  };

  const handleRemoveCustomTtsVoice = (index) => {
    setDraft((current) => {
      const nextVariants = (current?.voice_variants || []).filter((_, entryIndex) => entryIndex !== index);
      return {
        ...current,
        voice_variants: nextVariants.length > 0 ? nextVariants : [createEmptyCustomTtsVoice()],
      };
    });
  };

  const handleSave = async () => {
    if (!draft) return;

    if (isCreatingVoice && isCustomTtsDraft) {
      const voiceVariants = (draft.voice_variants || []).map((entry) => ({
        local_voice_name: String(entry.local_voice_name || '').trim(),
        provider_voice: String(entry.provider_voice || '').trim(),
        gender: String(entry.gender || '').trim(),
        actual_pro_voices: String(entry.actual_pro_voices || '').trim(),
      }));

      if (voiceVariants.length === 0 || voiceVariants.some((entry) => !entry.local_voice_name)) {
        toast.error('Each custom TTS voice entry needs a local voice name.');
        return;
      }
    } else if (!String(draft.local_voice_name || '').trim()) {
      toast.error('Local voice name is required.');
      return;
    }

    if (!(isCreatingVoice && isCustomTtsDraft)) {
      const overrideProvider = String(draft.override?.provider || '').trim();
      const overrideVoice = String(draft.override?.provider_voice || '').trim();
      if ((overrideProvider || overrideVoice) && !(overrideProvider && overrideVoice)) {
        toast.error('The override needs both a provider and a provider voice ID.');
        return;
      }
      if (draft.override_provider && !(overrideProvider && overrideVoice)) {
        toast.error('Fill in the override provider and voice ID, or turn the override off.');
        return;
      }
    }

    try {
      setSaving(true);
      if (isCreatingVoice && isCustomTtsDraft) {
        const response = await axios.post(`${apiUrl}/api/v1/admin/voices/custom-tts`, {
          active: !!draft.active,
          provider: draft.provider || null,
          type: draft.type || 'gpu-tts',
          model_name: draft.model_name || null,
          token_id: draft.token_id || null,
          voices: (draft.voice_variants || []).map((entry) => ({
            local_voice_name: String(entry.local_voice_name || '').trim(),
            provider_voice: String(entry.provider_voice || '').trim() || null,
            gender: String(entry.gender || '').trim() || null,
            actual_pro_voices: String(entry.actual_pro_voices || '').trim() || null,
          })),
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const createdVoices = Array.isArray(response.data?.voices) ? response.data.voices : [];
        if (createdVoices.length === 0) {
          throw new Error('No voices were created');
        }

        const firstCreatedVoice = createdVoices[0];
        setSelectedVoice(firstCreatedVoice);
        setDraft({
          ...firstCreatedVoice,
          __isNew: false,
          __createMode: 'custom_tts',
          voice_variants: [createEmptyCustomTtsVoice()],
        });
        setCreateDraftMode('custom_tts');
        setSelectedVoiceId(firstCreatedVoice.id);
        setVoices((current) => [...createdVoices, ...current]);
        toast.success(`${createdVoices.length} custom TTS voice${createdVoices.length === 1 ? '' : 's'} created.`);
      } else {
        const payload = {
          local_voice_name: draft.local_voice_name,
          active: !!draft.active,
          provider: draft.provider || null,
          provider_voice: draft.provider_voice || null,
          gender: draft.gender || null,
          actual_pro_voices: draft.actual_pro_voices || null,
          type: draft.type || null,
          model_name: draft.model_name || null,
          token_id: draft.token_id || null,
          // Free-text language hint passed to the TTS provider (Cartesia).
          // Empty string clears it; the agent then defaults to Hindi.
          language: String(draft.language || '').trim(),
          // Empty string clears it (update_voice treats null as "leave unchanged").
          preview_url: String(draft.preview_url || '').trim(),
          // Override: when on, the agent tries this provider/voice first, then this
          // voice's own provider. Both fields empty clears a saved override.
          override_provider: !!draft.override_provider,
          override: {
            provider: String(draft.override?.provider || '').trim().toLowerCase(),
            provider_voice: String(draft.override?.provider_voice || '').trim(),
          },
        };

        const response = isCreatingVoice
          ? await axios.post(`${apiUrl}/api/v1/admin/voices`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          })
          : await axios.patch(`${apiUrl}/api/v1/admin/voices/${selectedVoiceId}`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

        const updatedVoice = response.data;
        setSelectedVoice(updatedVoice);
        setDraft({ ...updatedVoice, __isNew: false, __createMode: CUSTOM_TTS_TYPES.has(String(updatedVoice.type || '').toLowerCase()) ? 'custom_tts' : 'voice' });
        setCreateDraftMode(CUSTOM_TTS_TYPES.has(String(updatedVoice.type || '').toLowerCase()) ? 'custom_tts' : 'voice');
        setSelectedVoiceId(updatedVoice.id);
        setVoices((current) => {
          if (isCreatingVoice) {
            return [updatedVoice, ...current];
          }
          return current.map((entry) => (
            entry.id === updatedVoice.id ? updatedVoice : entry
          ));
        });
        toast.success(isCreatingVoice ? 'Voice created.' : 'Voice settings updated.');
      }
    } catch (error) {
      console.error('Failed to save voice', error);
      toast.error(error.response?.data?.detail || 'Failed to save voice settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVoiceId || selectedVoiceId === NEW_VOICE_ID) return;
    const deletedId = selectedVoiceId;
    try {
      setDeleting(true);
      await axios.delete(`${apiUrl}/api/v1/admin/voices/${deletedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const remaining = voices.filter((entry) => entry.id !== deletedId);
      setVoices(remaining);
      setSelectedVoice(null);
      setDraft(null);
      setSelectedVoiceId(remaining[0]?.id || '');
      setConfirmDeleteOpen(false);
      toast.success('Voice deleted.');
    } catch (error) {
      console.error('Failed to delete voice', error);
      toast.error(error.response?.data?.detail || 'Failed to delete voice.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-6">
      

      <main className="flex-1 min-h-0 p-6 overflow-y-auto  lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <Mic className="w-3.5 h-3.5" />
                Voice Controls
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Voice Manager</h1>
              <p className="mt-1 text-slate-500">Control and manage system-wide voice identities and provider mappings.</p>
            </div>

            <Button
              variant="outline"
              onClick={fetchVoices}
              disabled={loadingVoices}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loadingVoices ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh Voices
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <div className="space-y-3">
                  <Button
                    onClick={() => handleCreateDraft('voice')}
                    className="w-full text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Voice
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCreateDraft('custom_tts')}
                    className="w-full border-violet-200 text-violet-700 hover:bg-violet-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Custom TTS
                  </Button>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search voices..."
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="max-h-[72vh] overflow-y-auto">
                {loadingVoices ? (
                  <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading voices...
                  </div>
                ) : filteredVoices.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-center text-slate-500">
                    No voices match the current search.
                  </div>
                ) : (
                  filteredVoices.map((entry) => (
                    <div
                      key={entry.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedVoiceId(entry.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedVoiceId(entry.id);
                        }
                      }}
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 cursor-pointer ${
                        selectedVoiceId === entry.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-violet-100 text-violet-700">
                        <Mic className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate text-slate-900">{entry.local_voice_name || 'Unnamed Voice'}</div>
                        <div className="text-sm truncate text-slate-500">{entry.provider ? `Provider: ${entry.provider}` : 'No Provider'}</div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          {CUSTOM_TTS_TYPES.has(String(entry.type || '').toLowerCase()) ? (
                            <span className="rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">GPU-tts</span>
                          ) : null}
                          <span className={`rounded-full px-2 py-0.5 font-medium ${entry.actual_pro_voices ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{entry.gender || 'Unknown'}</span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${entry.active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            {entry.active ? 'Active' : 'Inactive'}
                          </span>
                          {entry.override_provider && entry.override?.provider ? (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                              Override: {entry.override.provider}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); handlePreviewPlay(entry.id, entry.preview_url); }}
                        disabled={!entry.preview_url}
                        aria-label="Preview voice"
                        className="flex items-center justify-center w-8 h-8 shrink-0 rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                      >
                        {playingPreviewId === entry.id ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white border rounded-2xl border-slate-200 shadow-sm">
              {loadingVoice || !draft ? (
                <div className="flex min-h-[520px] items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {selectedVoiceId ? 'Loading voice settings...' : 'Select a voice to manage.'}
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div className="flex flex-col gap-4 pb-6 border-b border-slate-100 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{draft.local_voice_name || (isCustomTtsDraft ? 'Create Custom TTS' : 'Create Voice')}</h2>
                      <p className="mt-1 text-sm text-slate-500">{draft.provider ? `Provider: ${draft.provider}` : 'Fill the voice details and save to create a new record.'}</p>
                      {!isCreatingVoice ? (
                        <div className="flex flex-wrap gap-2 mt-3 text-xs">
                          <span className="px-3 py-1 font-medium rounded-full bg-slate-100 text-slate-600">ID: {draft.id}</span>
                          {CUSTOM_TTS_TYPES.has(String(draft.type || '').toLowerCase()) ? (
                            <span className="px-3 py-1 font-medium rounded-full bg-violet-50 text-violet-700">GPU-tts</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {!isCreatingVoice ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setConfirmDeleteOpen(true)}
                          disabled={saving || deleting}
                          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Delete
                        </Button>
                      ) : null}
                      <Button
                        onClick={handleSave}
                        disabled={saving || deleting}
                        className="text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {isCreatingVoice ? (isCustomTtsDraft ? 'Create Custom TTS' : 'Create Voice') : 'Save Changes'}
                      </Button>
                    </div>
                  </div>

                  <AlertDialog open={confirmDeleteOpen} onOpenChange={(open) => { if (!deleting) setConfirmDeleteOpen(open); }}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this voice?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {`This will permanently delete "${draft.local_voice_name || 'this voice'}". This action cannot be undone, and the voice will no longer be available for agents or test calls.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>No, cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={(event) => { event.preventDefault(); handleDelete(); }}
                          disabled={deleting}
                          className="text-white bg-red-600 hover:bg-red-700 focus:ring-red-600"
                        >
                          {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Yes, delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  {isCustomTtsDraft ? (
                    <div className="space-y-4 rounded-2xl border border-violet-100 bg-violet-50/50 p-5">
                      <div>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-violet-700">Model & Provider</h3>
                        <p className="mt-1 text-sm text-slate-500">Optional GPU-backed custom TTS details to store alongside this voice.</p>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label>Model Name</Label>
                          <Input value={draft.model_name || ''} onChange={(event) => handleFieldChange('model_name', event.target.value)} placeholder="e.g. xtts-v2" />
                        </div>
                        <div className="space-y-2">
                          <Label>Provider</Label>
                          <Input value={draft.provider || ''} onChange={(event) => handleFieldChange('provider', event.target.value)} placeholder="e.g. Coqui" />
                        </div>
                        <div className="space-y-2">
                          <Label>Token ID</Label>
                          <Input value={draft.token_id || ''} onChange={(event) => handleFieldChange('token_id', event.target.value)} placeholder="sk-********" />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {isCreatingVoice && isCustomTtsDraft ? (
                    <div className="space-y-5">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Voice Settings</h3>
                          <p className="mt-1 text-sm text-slate-500">Each voice below will create its own document in the `voice` collection.</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleAddCustomTtsVoice}
                          className="border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Voice
                        </Button>
                      </div>

                      <div className="space-y-6">
                        {(draft.voice_variants || []).map((voiceEntry, index) => (
                          <div key={`custom-tts-voice-${index}`} className="space-y-4 border-t border-slate-100 pt-5 first:border-t-0 first:pt-0">
                            <div className="flex items-center justify-between gap-4">
                              <div className="text-sm font-medium text-slate-900">Voice {index + 1}</div>
                              {(draft.voice_variants || []).length > 1 ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => handleRemoveCustomTtsVoice(index)}
                                  className="h-8 px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              ) : null}
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label>Local Voice Name</Label>
                                <Input
                                  value={voiceEntry.local_voice_name || ''}
                                  onChange={(event) => handleCustomTtsVoiceFieldChange(index, 'local_voice_name', event.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Provider Voice ID</Label>
                                <Input
                                  value={voiceEntry.provider_voice || ''}
                                  onChange={(event) => handleCustomTtsVoiceFieldChange(index, 'provider_voice', event.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Gender</Label>
                                <Select
                                  value={voiceEntry.gender || GENDER_UNSET_VALUE}
                                  onValueChange={(value) => handleCustomTtsVoiceFieldChange(
                                    index,
                                    'gender',
                                    value === GENDER_UNSET_VALUE ? '' : value,
                                  )}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select gender" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={GENDER_UNSET_VALUE}>Select gender</SelectItem>
                                    {GENDER_OPTIONS.map((gender) => (
                                      <SelectItem key={gender} value={gender}>{gender}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Actual Pro Voices</Label>
                                <Input
                                  value={voiceEntry.actual_pro_voices || ''}
                                  onChange={(event) => handleCustomTtsVoiceFieldChange(index, 'actual_pro_voices', event.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Local Voice Name</Label>
                        <Input value={draft.local_voice_name || ''} onChange={(event) => handleFieldChange('local_voice_name', event.target.value)} />
                      </div>
                      {!isCustomTtsDraft ? (
                        <div className="space-y-2">
                          <Label>Provider</Label>
                          <Input value={draft.provider || ''} onChange={(event) => handleFieldChange('provider', event.target.value)} />
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <Label>Provider Voice ID</Label>
                        <Input value={draft.provider_voice || ''} onChange={(event) => handleFieldChange('provider_voice', event.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <Select
                          value={draft.gender || GENDER_UNSET_VALUE}
                          onValueChange={(value) => handleFieldChange(
                            'gender',
                            value === GENDER_UNSET_VALUE ? '' : value,
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={GENDER_UNSET_VALUE}>Select gender</SelectItem>
                            {GENDER_OPTIONS.map((gender) => (
                              <SelectItem key={gender} value={gender}>{gender}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Actual Pro Voices</Label>
                        <Input value={draft.actual_pro_voices || ''} onChange={(event) => handleFieldChange('actual_pro_voices', event.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Language</Label>
                        <Input
                          value={draft.language || ''}
                          onChange={(event) => handleFieldChange('language', event.target.value)}
                          placeholder="Type a language code, e.g. hi, en, te, ta"
                        />
                        <p className="text-xs text-slate-500">
                          Free text. Passed to the TTS provider (Cartesia) as the synthesis language. Blank falls back to Hindi.
                        </p>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Preview URL</Label>
                        <div className="flex gap-2">
                          <Input
                            value={draft.preview_url || ''}
                            onChange={(event) => handleFieldChange('preview_url', event.target.value)}
                            placeholder="Enter URL"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handlePreviewPlay(draft.id || NEW_VOICE_ID, draft.preview_url)}
                            disabled={!draft.preview_url}
                            className="border-slate-200 text-slate-700 hover:bg-slate-50"
                          >
                            {playingPreviewId === (draft.id || NEW_VOICE_ID) ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          URL to a sample audio clip for this voice, played by the preview button here and in the batch scheduler.
                        </p>
                      </div>
                      <div className="space-y-4 p-4 border rounded-xl border-slate-200 md:col-span-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-medium text-slate-900">Override provider</div>
                            <p className="mt-1 text-sm text-slate-500">
                              When on, calls try the override provider and voice first. If it is down, the call uses this
                              voice&apos;s own provider; if that is down too, it uses the default voice for the language below.
                              Takes effect only once the agents have the provider chain turned on. Switching it off keeps the
                              saved override but ignores it.
                            </p>
                          </div>
                          <Switch
                            checked={!!draft.override_provider}
                            onCheckedChange={(checked) => handleFieldChange('override_provider', checked)}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Override provider</Label>
                            <Select
                              value={draft.override?.provider || OVERRIDE_PROVIDER_UNSET_VALUE}
                              onValueChange={(value) => handleOverrideFieldChange(
                                'provider',
                                value === OVERRIDE_PROVIDER_UNSET_VALUE ? '' : value,
                              )}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={OVERRIDE_PROVIDER_UNSET_VALUE}>None</SelectItem>
                                {OVERRIDE_PROVIDER_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Override provider voice ID</Label>
                            <Input
                              value={draft.override?.provider_voice || ''}
                              onChange={(event) => handleOverrideFieldChange('provider_voice', event.target.value)}
                              placeholder="Voice ID at the override provider"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">
                          The override speaks the language set on this voice (Language field above).
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Activation Status</h3>
                      <p className="text-sm text-slate-500">Toggle this to enable or disable the voice across the platform.</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      <div className="p-4 border rounded-xl border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-medium text-slate-900">Active</div>
                            <p className="mt-1 text-sm text-slate-500">If disabled, this voice will not be available for agents or test calls.</p>
                          </div>
                          <Switch
                            checked={!!draft.active}
                            onCheckedChange={(checked) => handleFieldChange('active', checked)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
